import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import apiClient from '@/services/apiClient';

const STAFF_BADGE_COLORS = [
  'bg-blue-50 text-blue-700 border-blue-200',
  'bg-purple-50 text-purple-700 border-purple-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-rose-50 text-rose-700 border-rose-200',
];

const FoldingHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search.trim()) params.append('search', search.trim());
      const res = await apiClient.get(`/folding/history?${params}`);
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, [page]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="folding-history-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse/folding')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg font-bold text-gray-900">Folding History</h1>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchHistory()}
            placeholder="Search by folding ID, bundle code, or staff name..."
            data-testid="search-input"
          />
          <Button onClick={fetchHistory} data-testid="search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </div>

        <Card>
          <CardContent className="pt-4">
            {loading ? (
              <p className="text-center text-gray-400 py-8">Loading...</p>
            ) : transactions.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No folding transactions found</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Folding ID</TableHead>
                      <TableHead>Staff & Qty Breakdown</TableHead>
                      <TableHead className="text-center">Bundles</TableHead>
                      <TableHead className="text-center">Total USNs</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((t) => {
                      const breakdown = (t.staff_breakdown && t.staff_breakdown.length > 0)
                        ? t.staff_breakdown
                        : [t.staff_1, t.staff_2, t.staff_3].filter((s) => s && s.staff_id);
                      return (
                        <TableRow key={t.folding_id}>
                          <TableCell className="font-mono text-xs">{t.folding_id}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Users className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              {breakdown.map((s, i) => (
                                <Badge
                                  key={s.staff_id || i}
                                  className={STAFF_BADGE_COLORS[i % STAFF_BADGE_COLORS.length]}
                                  variant="outline"
                                >
                                  {s.staff_name || '-'} · {s.attributed_qty || 0}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{t.total_bundles}</TableCell>
                          <TableCell className="text-center font-bold">{t.total_usn_count}</TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {total > 20 && (
              <div className="flex justify-between items-center mt-4 pt-3 border-t">
                <span className="text-sm text-gray-500">Page {page + 1} of {Math.ceil(total / 20)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 20 >= total}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FoldingHistory;
